import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../common/Modal';
import { Customer, CreateCustomerData, CustomerFormData, CustomerType, ContactPerson } from '../../types/customer';
import ContactPersonsManager from './ContactPersonsManager';
import {
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
  IdentificationIcon,
  DocumentIcon,
  XMarkIcon,
  PaperClipIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  PlusIcon,
  ArrowPathIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStorage } from '../../hooks/useStorage';
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomFieldsRenderer from '../common/CustomFieldsRenderer';
import { Tooltip } from '../common/Tooltip';
import { Timestamp, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { formatDocumentDate } from 'utils/formatters';
import { db } from '../../lib/firebase';
import { DatePicker } from '../common/DatePicker';
import DocumentPreviewModal from '../preview/DocumentPreviewModal';
import { formatTaxNumber, formatRegistrationNumber } from '../../utils/validation';
import { toast } from 'react-hot-toast';

// Draft storage constants
const DRAFT_STORAGE_KEY = 'customer_form_draft';
const DRAFT_TIMESTAMP_KEY = 'customer_form_draft_timestamp';

interface CustomerFormProps {
  onSubmit: (data: CreateCustomerData) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Customer | null;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { organizations } = useOrganizations();
  const { t } = useLanguage();
  const { uploadFile, deleteFile } = useStorage();
  const { customFields } = useCustomFields('customer');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocumentToEdit, setSelectedDocumentToEdit] = useState<any>(null);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(initialData?.customFields || {});
  
  // Egyszerű jelölés, hogy meglévő kapcsolat-e az ügyfél
  const [isExistingRelationship, setIsExistingRelationship] = useState(false);
  
  // Draft recovery state
  const [hasDraft, setHasDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState<Date | null>(null);
  
  // Contact persons state - minden ügyfélnek egyedi kapcsolattartói
  const [contactPersons, setContactPersons] = useState<ContactPerson[]>([]);

  const defaultValues = {
    type: 'individual' as CustomerType,
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    taxNumber: '',
    registrationNumber: '',
    organizations: [],
    notes: '',
    isActive: true,
    documents: [],
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    contactPerson: {
      name: '',
      email: '',
      phone: '',
      position: '',
      fax: ''
    },
    bankAccount: '',
    customFields: {}
  };

  const { register, handleSubmit, watch, setValue, reset, getValues } = useForm<CustomerFormData>({
    defaultValues: initialData || defaultValues
  });

  // Amikor initialData-val inicializáljuk a formot, biztosítsuk a helyes sorrendet
  React.useEffect(() => {
    if (isOpen && initialData) {
      console.log("Setting up form for editing with proper field order");
      reset(initialData);
      // Frissítjük a contactPersons state-et is
      setContactPersons(initialData.contactPersons || []);
      // Frissítjük a customFields state-et is
      setCustomFieldValues(initialData.customFields || {});
    } else if (isOpen && !initialData) {
      // Új ügyfél esetén üres tömb és üres customFields
      setContactPersons([]);
      setCustomFieldValues({});
    }
  }, [initialData, reset, isOpen]);

  // Test localStorage accessibility
  useEffect(() => {
    try {
      localStorage.setItem('test_storage', 'test');
      // console.log('localStorage elérhető:', localStorage.getItem('test_storage'));
      localStorage.removeItem('test_storage');
    } catch (error) {
      console.error('localStorage nem érhető el:', error);
    }
  }, []);

  // Draft recovery functions
  const saveDraft = (data: any) => {
    try {
      // Mentjük a teljes form adatot, hogy minden mező visszaállítható legyen
      const draftToSave = {
        ...data,
        // Biztosítsuk, hogy ezek a tömbök léteznek, ha hiányoznának
        documents: Array.isArray(data.documents) ? data.documents : [],
        organizations: Array.isArray(data.organizations) ? data.organizations : [],
        // Ellenőrizzük a beágyazott objektumokat is
        address: data.address || {},
        contactPerson: data.contactPerson || {}
      };
      
      const draftString = JSON.stringify(draftToSave);
      console.log("Saving complete draft to localStorage:", draftToSave);
      console.log("Draft string length:", draftString.length);
      
      localStorage.setItem(DRAFT_STORAGE_KEY, draftString);
      localStorage.setItem(DRAFT_TIMESTAMP_KEY, new Date().toISOString());
      
      // Ellenőrizzük, hogy sikeres volt-e a mentés
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      console.log("Saved draft successfully:", !!savedDraft);
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const loadDraft = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
      
      console.log("Loading draft from localStorage");
      console.log("Draft exists:", !!draftData);
      console.log("Timestamp exists:", !!timestamp);
      
      if (draftData && timestamp) {
        try {
          const parsedData = JSON.parse(draftData);
          console.log("Parsed draft data:", parsedData);
          setDraftTimestamp(new Date(timestamp));
          
          // Biztosítsuk, hogy minden szükséges tulajdonság inicializálva legyen
          const safeData = {
            ...parsedData,
            // Biztosítsuk, hogy a documents tömb mindig létezzen
            documents: Array.isArray(parsedData.documents) ? parsedData.documents : []
          };
          
          console.log("Safe draft data:", safeData);
          return safeData;
        } catch (parseError) {
          console.error("Error parsing draft data:", parseError);
          return null;
        }
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
    return null;
  };

  const clearDraft = () => {
    try {
      console.log("Clearing draft");
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      setHasDraft(false);
      setDraftTimestamp(null);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  };

  const restoreDraft = () => {
    try {
      const draftData = loadDraft();
      if (draftData) {
        console.log("Restoring draft data:", draftData);
        
        // Biztosítsuk, hogy minden szükséges tulajdonság inicializálva legyen
        const completeData = {
          ...defaultValues,
          ...draftData,
          // Biztosítsuk, hogy a tömbök mindig létezzenek
          documents: Array.isArray(draftData.documents) ? draftData.documents : [],
          organizations: Array.isArray(draftData.organizations) ? draftData.organizations : [],
          // Biztosítsuk, hogy a beágyazott objektumok mindig létezzenek
          address: { ...defaultValues.address, ...(draftData.address || {}) },
          contactPerson: { ...defaultValues.contactPerson, ...(draftData.contactPerson || {}) }
        };
        
        console.log("Complete data for restoration:", completeData);
        
        // Állítsuk be a form adatait
        reset(completeData);
        
        setHasDraft(false);
        clearDraft();
      }
    } catch (error) {
      console.error("Error restoring draft:", error);
      toast.error("Hiba történt a vázlat visszaállítása közben");
      clearDraft();
    }
  };

  // Draft recovery banner component
  const DraftRecoveryBanner = () => {
    if (!hasDraft || !draftTimestamp) return null;
    
    // Format the date for display
    const formattedDate = new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(draftTimestamp);
    
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <DocumentTextIcon className="h-5 w-5 text-amber-600 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">Mentett vázlat elérhető</h3>
            <p className="text-xs text-amber-700">
              {`Egy mentett vázlat elérhető (${formattedDate}). Szeretné visszaállítani?`}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={restoreDraft}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
            Visszaállítás
          </button>
          <button
            type="button"
            onClick={clearDraft}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
          >
            <XMarkIcon className="h-3.5 w-3.5 mr-1" />
            Elvetés
          </button>
        </div>
      </div>
    );
  };

  // Register documents field
  React.useEffect(() => {
    register('documents');
  }, [register]);

  // Reset form when initialData changes and check for draft when form opens
  React.useEffect(() => {
    if (isOpen) {
      console.log("Form opened, initialData:", initialData ? "exists" : "doesn't exist");
      
      // Közvetlenül ellenőrizzük a localStorage-ban tárolt piszkozatot
      try {
        const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
        const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
        
        console.log("Direct localStorage check - Draft exists:", !!draftData);
        console.log("Direct localStorage check - Timestamp exists:", !!timestamp);
        
        if (draftData && timestamp) {
          try {
            const parsedData = JSON.parse(draftData);
            console.log("Parsed draft data:", parsedData);
            
            // Ha nincs initialData (új ügyfél) és van piszkozat, beállítjuk a hasDraft-ot
            if (!initialData) {
              console.log("Setting hasDraft to true directly");
              setHasDraft(true);
              setDraftTimestamp(new Date(timestamp));
              
              // Biztosítsuk, hogy a documents tömb létezik
              if (!parsedData.documents) parsedData.documents = [];
            }
          } catch (parseError) {
            console.error("Error parsing draft data:", parseError);
          }
        } else {
          console.log("No draft found in localStorage");
          setHasDraft(false);
        }
      } catch (localStorageError) {
        console.error("Error accessing localStorage:", localStorageError);
      }
      
      // Check for existing draft when form opens (a korábbi metódus megtartása is)
      if (!initialData) {
        const draftData = loadDraft();
        if (draftData) {
          console.log("Setting hasDraft to true");
          setHasDraft(true);
        } else {
          setHasDraft(false);
        }
      } else {
        // If editing existing customer, clear any drafts
        clearDraft();
      }
      
      // Ha van initialData, akkor beállítjuk az űrlapot
      if (initialData) {
        console.log("Setting up form with initialData");
        reset(initialData);
        setIsExistingRelationship(false);
      } else {
        reset(defaultValues);
      }
    }
  }, [initialData, reset, isOpen]);

  // Auto-save draft with a timer (only for new customers)
  useEffect(() => {
    if (!isOpen || initialData) return;
    
    console.log("Starting auto-save timer");
    
    // Azonnali mentés az űrlap megnyitásakor
    const saveData = () => {
      const currentValues = getValues();
      // Don't save empty forms
      if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
        console.log("Auto-saving draft via timer");
        saveDraft(currentValues);
      } else {
        console.log("No data to save yet");
      }
    };
    
    // Első mentés rövid késleltetéssel
    const initialSaveTimeout = setTimeout(() => {
      saveData();
    }, 3000);
    
    // Set up interval to save draft periodically
    const saveInterval = setInterval(saveData, 10000); // Save every 10 seconds
    
    return () => {
      clearTimeout(initialSaveTimeout);
      clearInterval(saveInterval);
      console.log("Cleared auto-save timer");
    };
  }, [isOpen, initialData, getValues]);
  
  // Manuálisan indítsuk el a mentést form mezők változásakor
  const watchedFields = [
    watch('companyName'), 
    watch('firstName'), 
    watch('lastName'), 
    watch('email')
  ];
  
  useEffect(() => {
    if (!isOpen || initialData) return;
    
    // Csak akkor futtassuk, ha az űrlap már nyitva van és van adat
    const currentValues = getValues();
    if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
      console.log("Field changed, triggering save");
      // Kis késleltetéssel, hogy ne minden billentyűleütésnél mentsen
      const changeTimeout = setTimeout(() => {
        saveDraft(currentValues);
      }, 1000);
      
      return () => clearTimeout(changeTimeout);
    }
  }, [watchedFields, isOpen, initialData, getValues]);

  // Save draft when modal is closed without submitting or page is unloaded
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isOpen && !initialData) {
        const currentValues = getValues();
        // Don't save empty forms
        if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
          console.log("Saving draft before unload");
          saveDraft(currentValues);
          // Standard message for old browsers
          e.preventDefault();
          e.returnValue = '';
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, initialData, getValues]);

  // Formázott értékek beállítása a mezőkben
  React.useEffect(() => {
    // Biztosítsuk, hogy csak a form betöltése után futtassuk
    const formatAllConsumptionFields = () => {
      // This function is now empty since we removed all consumption fields
    };
    
    // A form betöltése után kis késleltetéssel formázzuk az értékeket
    const timeoutId = setTimeout(() => {
      formatAllConsumptionFields();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, initialData]); // Csak a form megnyitásakor és a initialData változásakor futtatjuk

  const customerType = watch('type');

  // Document upload handler
  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Generáljunk egyedi fájlnevet az ütközések elkerülése érdekében
      const timestamp = Date.now();
      const uniqueFileName = `${timestamp}_${file.name}`;

      // Ideiglenes vagy valós ügyfél azonosító használata
      const customerId = initialData?.id || 'temp-' + timestamp;
      const path = `customers/${customerId}/documents/${uniqueFileName}`;

      console.log('Uploading document to path:', path);
      const fileUrl = await uploadFile(file, path);

      const currentDocuments = watch('documents');
      const newDocument = {
        id: timestamp.toString(),
        name: uniqueFileName, // Tároljuk az egyedi fájlnevet, hogy később törölni tudjuk
        originalName: file.name, // Az eredeti fájlnév megjelenítéshez
        url: fileUrl,
        uploadedAt: Timestamp.now(),
        type: 'other' // Alapértelmezett típus, amelyet a dokumentumtípus kiválasztó fogja felülírni
      };

      // Frissítsük a form állapotát
      const updatedDocuments = [...currentDocuments, newDocument];
      setValue('documents', updatedDocuments);

      // Új feltöltött dokumentum megjelenítése a felhasználói felületen és a típus kiválasztás megnyitása
      setSelectedDocumentToEdit(newDocument);

      // Ha már van mentett ügyfél, frissítsük az adatbázist is
      if (initialData?.id) {
        try {
          const customerRef = doc(db, 'customers', initialData.id);
          await updateDoc(customerRef, {
            documents: updatedDocuments,
            updatedAt: serverTimestamp()
          });
          console.log('Document reference added to database');
        } catch (dbError) {
          console.error('Database update error:', dbError);
        }
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setIsUploading(false);
      // Reset the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Delete document handler
  const handleDocumentDelete = async (documentId: string) => {
    const documents = watch('documents');
    const documentToDelete = documents.find(doc => doc.id === documentId);

    if (!documentToDelete) return;

    try {
      // Frissítsük először a form állapotát, hogy a felhasználó azonnal visszajelzést kapjon
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      setValue('documents', updatedDocuments);

      // Ha meglévő ügyfélről van szó, frissítsük az adatbázist is
      if (initialData?.id) {
        try {
          // Töröljük a dokumentum referenciát az adatbázisból
          const customerRef = doc(db, 'customers', initialData.id);
          await updateDoc(customerRef, {
            documents: updatedDocuments,
            updatedAt: serverTimestamp()
          });
          console.log('Document reference removed from database');

          // Csak ezután próbáljuk törölni a fájlt a tárolóból
          const path = `customers/${initialData.id}/documents/${documentToDelete.name}`;
          await deleteFile(path);
          console.log('Document file deleted from storage');
        } catch (error) {
          console.error('Error updating database or deleting file:', error);
          // A hibát elnyeljük, mert a felhasználói élmény szempontjából
          // már megtörtént a dokumentum törlése a felületen
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      // Ha hiba történt a form frissítésénél, próbáljuk visszaállítani
      setValue('documents', documents);
    }
  };

  // Dokumentum típus változtatása
  const handleDocumentTypeChange = (documentId: string, type: string) => {
    const documents = watch('documents');
    const updatedDocuments = documents.map(doc =>
      doc.id === documentId ? { ...doc, type } : doc
    );

    setValue('documents', updatedDocuments);

    // Ha már van mentett ügyfél, frissítsük az adatbázist is
    if (initialData?.id) {
      try {
        const customerRef = doc(db, 'customers', initialData.id);
        updateDoc(customerRef, {
          documents: updatedDocuments,
          updatedAt: serverTimestamp()
        });
        console.log('Document type updated in database');
      } catch (error) {
        console.error('Error updating document type:', error);
      }
    }

    // Bezárjuk a dokumentum típus választó modált
    setSelectedDocumentToEdit(null);
  };

  // Checkbox handler a több érték kezeléséhez
  const handleOrganizationChange = (orgId: string, checked: boolean) => {
    const currentOrgs = watch('organizations') || [];
    const newOrgs = checked
      ? [...currentOrgs, orgId]
      : currentOrgs.filter(id => id !== orgId);
    setValue('organizations', newOrgs);
  };

  const handleDocumentClick = (e: React.MouseEvent<HTMLButtonElement>, document: any) => {
    e.preventDefault();
    e.stopPropagation();
    // Átalakítjuk a dokumentumot a megfelelő formátumra
    const previewDoc = {
      ...document,
      fileUrl: document.url,
      name: document.originalName || document.name
    };
    setPreviewDocument(previewDoc);
    setIsPreviewOpen(true);
  };

  // Handle tax number formatting
  const handleTaxNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatTaxNumber(e.target.value);
    setValue('taxNumber', formattedValue);
  };

  // Handle registration number formatting
  const handleRegistrationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatRegistrationNumber(e.target.value);
    setValue('registrationNumber', formattedValue);
  };

  // Form submission
  const onSubmitHandler = async (data: CustomerFormData) => {
    try {
      console.log("========== FORM SUBMIT START ==========");
      console.log("1. Form data from react-hook-form:", data);
      console.log("2. ContactPersons state:", contactPersons);
      console.log("3. ContactPersons count:", contactPersons.length);
      
      // Készítünk egy másolatot az adatokról, hogy ne módosítsuk közvetlenül a RHF adatait
      const submissionData = { 
        ...data,
        // Hozzáadjuk a contactPersons state-et a mentendő adatokhoz
        contactPersons: contactPersons,
        // Hozzáadjuk az egyedi mezőket
        customFields: customFieldValues
      };

      console.log("4. Submission data (with contactPersons):", submissionData);
      console.log("5. Has contactPersons field?", 'contactPersons' in submissionData);
      console.log("6. ContactPersons in submission:", submissionData.contactPersons);
      console.log("========== SENDING TO SERVICE ==========");

      // Törli a piszkozatot, mivel a form beküldésre került
      setHasDraft(false);
      setDraftTimestamp(null);
      
      // Törli a localStorage-ból a piszkozatot
      try {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      } catch (clearError) {
        console.error("Error clearing draft:", clearError);
      }

      onSubmit(submissionData as CreateCustomerData);
      console.log("========== FORM SUBMIT END ==========");
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('common.errors.somethingWentWrong'));
    }
  };

  // Modal bezárás kezelése
  const handleModalClose = () => {
    // Ha új ügyfelet rögzít és van értelmes adat, mentse el piszkozatként
    if (!initialData) {
      const currentValues = getValues();
      console.log("Modal close, current values:", currentValues);
      if (currentValues.companyName || currentValues.firstName || currentValues.lastName || currentValues.email) {
        console.log("Saving draft on modal close");
        saveDraft(currentValues);
        toast.success('A vázlat elmentve');
      }
    }
    
    onCancel();
  };

  // Debug funkció a localStorage ellenőrzéséhez
  useEffect(() => {
    if (isOpen) {
      // Közvetlen hozzáférés a localStorage-hoz
      try {
        // Tesztadat tárolása
        localStorage.setItem('customer_form_test', 'test_value');
        console.log('Test localStorage write successful');
        
        // Tesztadat olvasása
        const testValue = localStorage.getItem('customer_form_test');
        console.log('Test localStorage read result:', testValue);
        
        // Tesztadat törlése
        localStorage.removeItem('customer_form_test');
        console.log('Test localStorage delete successful');
        
        // Aktuális piszkozat ellenőrzése
        const currentDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
        const currentTimestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
        console.log('Current draft in storage:', currentDraft ? 'exists' : 'does not exist');
        console.log('Current timestamp in storage:', currentTimestamp);
      } catch (error) {
        console.error('Error accessing localStorage:', error);
      }
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={initialData ? t('customers.form.title.edit') : t('customers.form.title.new')}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6 p-6">
        {/* Draft Recovery Banner */}
        <DraftRecoveryBanner />
        
        {/* Customer Type Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <IdentificationIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('customers.form.type.title')}</h3>
              
              {/* Meglévő kapcsolat jelölőnégyzet - csak új ügyfél esetén jelenjen meg */}
              {!initialData && (
                <label className="relative flex items-center group mb-4">
                  <input
                    type="checkbox"
                    checked={isExistingRelationship}
                    onChange={(e) => setIsExistingRelationship(e.target.checked)}
                    className="sr-only peer opacity-0 absolute"
                  />
                  <div className="flex items-center px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 
                                peer-checked:border-green-600 peer-checked:bg-green-100 dark:peer-checked:bg-green-900/20
                                hover:border-green-300 dark:hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/10 cursor-pointer transition-all duration-200
                                shadow-sm peer-checked:shadow">
                    <div className="h-5 w-5 mr-3 rounded border border-green-400 dark:border-green-500
                                  flex items-center justify-center
                                  peer-checked:bg-green-600 peer-checked:border-green-600
                                  peer-checked:shadow-inner">
                      {isExistingRelationship && (
                        <svg className="h-3.5 w-3.5 text-yellow-300" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 peer-checked:text-green-800 dark:peer-checked:text-green-300 font-medium">
                      {t('customers.form.existingRelationship')}
                    </span>
                  </div>
                </label>
              )}
              
              <div className="flex gap-2">
                <label className="relative flex items-center group flex-1">
                  <input
                    type="radio"
                    value="individual"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                    <UserIcon className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400 peer-checked:text-primary-600 dark:peer-checked:text-primary-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                      {t('customers.form.type.individual')}
                    </span>
                  </div>
                </label>
                <label className="relative flex items-center group flex-1">
                  <input
                    type="radio"
                    value="organization"
                    {...register('type')}
                    className="sr-only peer"
                  />
                  <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                    <BuildingOffice2Icon className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400 peer-checked:text-primary-600 dark:peer-checked:text-primary-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                      {t('customers.form.type.organization')}
                    </span>
                  </div>
                </label>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">
                  {t('customers.form.organizations.title')}
                </h3>
                <div className="space-y-4">
                  {/* Organizations checkboxes */}
                  <div className="flex flex-wrap gap-2">
                    {organizations.map((org) => {
                      const isChecked = watch('organizations')?.includes(org.id) || false;
                      return (
                        <label
                          key={org.id}
                          className="relative flex items-center group"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => handleOrganizationChange(org.id, e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="flex items-center px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                                        peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20
                                        hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                            <BuildingOffice2Icon className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400 peer-checked:text-primary-600 dark:peer-checked:text-primary-400" />
                            <span className="text-sm text-gray-700 dark:text-gray-300 peer-checked:text-primary-700 dark:peer-checked:text-primary-300">
                              {org.name}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Notes field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.organizations.notes')}
                    </label>
                    <textarea
                      {...register('notes')}
                      rows={3}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                                 placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      placeholder={t('customers.form.organizations.notesPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Customer Info Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <IdentificationIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('customers.form.basicInfo.title')}</h3>

              {/* For organization type */}
              {watch('type') === 'organization' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.companyName')}
                    </label>
                    <input
                      type="text"
                      {...register('companyName')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.email')}
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.phone')}
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}

              {/* For individual type */}
              {watch('type') === 'individual' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.lastName')}
                    </label>
                    <input
                      type="text"
                      {...register('lastName')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.firstName')}
                    </label>
                    <input
                      type="text"
                      {...register('firstName')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.email')}
                    </label>
                    <input
                      type="email"
                      {...register('email')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.form.basicInfo.phone')}
                    </label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Company Data Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-100 to-sky-100 dark:from-blue-900/20 dark:to-sky-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <BuildingOffice2Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('customers.form.companyData.title')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.companyData.taxNumber')}
                  </label>
                  <input
                    type="text"
                    {...register('taxNumber')}
                    onChange={handleTaxNumberChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.companyData.registrationNumber')}
                  </label>
                  <input
                    type="text"
                    {...register('registrationNumber')}
                    onChange={handleRegistrationNumberChange}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/20 dark:to-violet-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <MapPinIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">{t('customers.form.address.title')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.address.street')}
                  </label>
                  <input
                    type="text"
                    {...register('address.street')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.address.city')}
                  </label>
                  <input
                    type="text"
                    {...register('address.city')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.address.postalCode')}
                  </label>
                  <input
                    type="text"
                    {...register('address.postalCode')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.address.country')}
                  </label>
                  <input
                    type="text"
                    {...register('address.country')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Kapcsolattartók - minden ügyfélnek egyedi */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-orange-100 to-red-100 dark:from-orange-900/20 dark:to-red-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <UserIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="p-4 flex-1">
              <ContactPersonsManager
                contactPersons={contactPersons}
                onChange={async (newContactPersons) => {
                  setContactPersons(newContactPersons);
                  
                  // Auto-save contact persons if editing existing customer
                  if (initialData?.id) {
                    try {
                      const { updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
                      const { db } = await import('../../lib/firebase');
                      
                      await updateDoc(doc(db, 'customers', initialData.id), {
                        contactPersons: newContactPersons,
                        updatedAt: serverTimestamp()
                      });
                      
                      console.log('Contact persons auto-saved');
                    } catch (error) {
                      console.error('Error auto-saving contact persons:', error);
                    }
                  }
                }}
                customer={initialData || undefined}
              />
            </div>
          </div>
        </div>

        {/* Szerződéses adatok */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-100 to-fuchsia-100 dark:from-purple-900/20 dark:to-fuchsia-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <DocumentTextIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {t('customers.form.contractDetails.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.form.contractDetails.bankAccount')}
                  </label>
                  <input
                    type="text"
                    {...register('bankAccount')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-200 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Document Upload Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
              <DocumentIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('customers.form.documents.title')}</h3>

              {/* Document Upload Input */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 
                                  rounded-md cursor-pointer transition-colors">
                  <PaperClipIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('customers.form.documents.uploadButton')}
                  </span>
                  <input
                    type="file"
                    onChange={handleDocumentUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
                {isUploading && (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {t('customers.form.documents.uploading')}
                  </span>
                )}
              </div>

              {/* Document List */}
              <div className="space-y-2">
                {!Array.isArray(watch('documents')) || watch('documents').length === 0 ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                    {t('customers.form.documents.empty')}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {watch('documents').map((document) => (
                      <li key={document.id} className="py-3 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          {document.type === 'contract' && <DocumentCheckIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />}
                          {document.type === 'quotation' && <DocumentChartBarIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />}
                          {(!document.type || document.type === 'other') && <DocumentIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />}
                          <div>
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {document.originalName || document.name}
                            </div>
                            <div className="flex items-center text-xs">
                              <span className="text-gray-600 dark:text-gray-400">
                                {formatDocumentDate(document.uploadedAt)}
                              </span>
                              <span className="mx-1.5 text-gray-400 dark:text-gray-500">•</span>
                              <button
                                onClick={(e) => handleDocumentClick(e, document)}
                                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                              >
                                {document.type === 'contract' && (t('customers.form.documents.typeContract') || 'Contract')}
                                {document.type === 'quotation' && (t('customers.form.documents.typeQuotation') || 'Quotation')}
                                {(!document.type || document.type === 'other') && (t('customers.form.documents.typeOther') || 'Other')}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Tooltip content={t('customers.form.documents.view')}>
                            <button
                              onClick={(e) => handleDocumentClick(e, document)}
                              className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                            >
                              <PaperClipIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>

                          <Tooltip content={t('customers.form.documents.delete')}>
                            <button
                              type="button"
                              onClick={() => handleDocumentDelete(document.id)}
                              className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Custom Fields Section */}
        {customFields.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <RectangleStackIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Egyedi mezők</h3>
                <CustomFieldsRenderer
                  customFields={customFields}
                  values={customFieldValues}
                  onChange={(key, value) => setCustomFieldValues(prev => ({ ...prev, [key]: value }))}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleModalClose}
            className="px-4 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          >
            {initialData ? t('common.save') : t('common.create')}
          </button>
        </div>
      </form>

      {/* Dokumentum típus választó modal */}
      <Modal
        isOpen={!!selectedDocumentToEdit}
        onClose={() => setSelectedDocumentToEdit(null)}
        title={t('customers.form.documents.selectType') || 'Select Document Type'}
        size="md"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t('customers.form.documents.selectTypeDescription') || 'Please select the type of this document:'}
          </p>

          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'contract')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
            >
              <div className="flex items-center">
                <DocumentCheckIcon className="w-5 h-5 mr-3 text-primary-600 dark:text-primary-400" />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t('customers.form.documents.typeContract') || 'Contract'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'contract' && (
                <div className="w-2 h-2 rounded-full bg-primary-500 dark:bg-primary-400"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'quotation')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
            >
              <div className="flex items-center">
                <DocumentChartBarIcon className="w-5 h-5 mr-3 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t('customers.form.documents.typeQuotation') || 'Quotation'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'quotation' && (
                <div className="w-2 h-2 rounded-full bg-primary-500 dark:bg-primary-400"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'other')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all"
            >
              <div className="flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-3 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t('customers.form.documents.typeOther') || 'Other Document'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'other' && (
                <div className="w-2 h-2 rounded-full bg-primary-500 dark:bg-primary-400"></div>
              )}
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => setSelectedDocumentToEdit(null)}
              className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Dokumentum előnézet modal */}
      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewDocument(null);
        }}
        contract={previewDocument}
      />
    </Modal>
  );
};

export default CustomerForm; 